// ai-capture-button.tsx — "✨ Phân tích bằng AI" opt-in button for inbox clarify.
// Calls the proxy, maps the result, fires onResult callback.
// Hidden when AI is off / signed-out / offline (caller controls visibility via aiAvailable).

import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { todayIso } from "../lib/date";
import { createAiClient } from "../lib/ai/ai-runtime";
import { buildCaptureRequest, mapCaptureResult } from "../lib/ai/capture-extract";
import type { CaptureResult } from "../lib/ai/capture-schema";
import type { ClarifyPrefill } from "./inbox-clarify-steps";
import type { InboxConversionType } from "../types";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  /** Raw inbox text to analyse. */
  text: string;
  /** Called with the mapped prefill + suggested type when AI succeeds. */
  onResult: (prefill: ClarifyPrefill, type: InboxConversionType) => void;
  /** When true, run the analysis automatically once on mount (no manual click). */
  autoRun?: boolean;
}

type ButtonState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

// ── Component ─────────────────────────────────────────────────────────────────

export function AiCaptureButton({ text, onResult, autoRun = false }: Props) {
  const [state, setState] = useState<ButtonState>(
    autoRun && text.trim() ? { kind: "loading" } : { kind: "idle" },
  );

  // Memoized so the auto-run effect has a stable dependency.
  const runAnalysis = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const client = createAiClient();
      const raw = await client.generateJson<CaptureResult>(buildCaptureRequest(text, todayIso()));
      const { prefill, type } = mapCaptureResult(raw);
      setState({ kind: "idle" });
      onResult(prefill, type);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Không phân tích được — vui lòng thử lại.";
      setState({ kind: "error", message });
    }
  }, [text, onResult]);

  // Auto-run once on mount when enabled (modal remounts per item → fires per item).
  // ranRef guards against duplicate runs (re-renders / StrictMode double-effect).
  const ranRef = useRef(false);
  useEffect(() => {
    if (!autoRun || ranRef.current || !text.trim()) return;
    ranRef.current = true;
    void runAnalysis();
  }, [autoRun, text, runAnalysis]);

  function handleClick() {
    if (state.kind === "loading") return;
    void runAnalysis();
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={state.kind === "loading"}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50"
      >
        {state.kind === "loading" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {state.kind === "loading" ? "Đang phân tích…" : "Phân tích bằng AI"}
      </button>

      {state.kind === "error" && (
        <div className="flex items-start gap-1.5 rounded-xl bg-rose-50 px-3 py-2 text-[11px] text-rose-600 dark:bg-rose-950/30 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{state.message}</span>
        </div>
      )}
    </div>
  );
}
